import { z } from 'zod';

import {
  emailInputSchema,
  mobileInputSchema,
  optionalInput,
  timestampSchema,
} from '@/types/schemas';

// --- Roles and permissions ------------------------------------------------------------

export const roleSchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** System roles (Admin, Member) are fixed by the code and cannot be edited or deleted. */
  isSystem: z.boolean(),
  permissions: z.array(z.string()),
  userCount: z.number(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Role = z.infer<typeof roleSchema>;

export const permissionInfoSchema = z.object({
  key: z.string(),
  group: z.string(),
  description: z.string(),
});
export type PermissionInfo = z.infer<typeof permissionInfoSchema>;

export const roleFormSchema = z.object({
  name: z.string().trim().min(1, 'Enter a role name').max(100, 'Use at most 100 characters'),
  description: z
    .string()
    .trim()
    .max(255, 'Use at most 255 characters')
    .transform((value) => (value === '' ? null : value)),
});

export interface RoleInput {
  name: string;
  description: string | null;
  permissions: string[];
}

// --- Staff accounts ---------------------------------------------------------------------

export const staffFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter a name').max(150, 'Use at most 150 characters'),
    roleId: z.string().regex(/^\d+$/, 'Choose a role').transform(Number),
    email: optionalInput(emailInputSchema),
    mobile: optionalInput(mobileInputSchema),
    password: optionalInput(
      z.string().min(8, 'Use at least 8 characters').max(128, 'Use at most 128 characters'),
    ),
  })
  .refine((values) => values.email !== null || values.mobile !== null, {
    path: ['email'],
    message: 'Give an email or a mobile number to sign in with',
  });
export type StaffFormOutput = z.output<typeof staffFormSchema>;
